/**
 * Query Schema - Single source of truth for task filtering
 *
 * This schema defines what can be queried and how. It's designed to:
 * - Work client-side (JS filtering) or server-side (SQL generation)
 * - Be UI-buildable via checkboxes/dropdowns
 * - Be human-editable as text expressions
 * - Version for future migrations
 */

// Schema version for migration support
export const QUERY_SCHEMA_VERSION = 1;

// ============================================================================
// Operators
// ============================================================================

export type ComparisonOperator =
  | 'eq'        // equals
  | 'neq'       // not equals
  | 'gt'        // greater than
  | 'gte'       // greater than or equal
  | 'lt'        // less than
  | 'lte';      // less than or equal

export type SetOperator =
  | 'in'        // value in set
  | 'notIn';    // value not in set

export type ArrayOperator =
  | 'hasAny'    // array contains any of values
  | 'hasAll'    // array contains all of values
  | 'isEmpty'   // array is empty
  | 'isNotEmpty'; // array is not empty

export type StringOperator =
  | 'contains'    // string contains substring
  | 'startsWith'  // string starts with
  | 'endsWith';   // string ends with

export type NullOperator =
  | 'isNull'      // field is null/undefined/empty
  | 'isNotNull';  // field has a value

export type QueryOperator =
  | ComparisonOperator
  | SetOperator
  | ArrayOperator
  | StringOperator
  | NullOperator;

// ============================================================================
// Filterable Fields - derived from Todo type
// ============================================================================

// Fields that can be filtered, with their types for validation
export interface FilterableFields {
  // Core fields
  title: string;
  description: string | undefined;
  status: string;
  horizon: string | null;

  // Dates
  deadline: string | null;
  created_time: string;
  last_modified_time: string;
  completed_date: string | null;

  // Relations
  parent_id: string | undefined;
  team_id: string | undefined;
  assigned_to_id: string | undefined;
  user_id: string;

  // Arrays
  tags: string[];

  // Other
  recurrence_interval: string;
  notes: string | undefined;
  source: string | undefined;
}

export type FilterableField = keyof FilterableFields;

// Field metadata for UI and validation
export interface FieldMeta {
  label: string;
  type: 'string' | 'date' | 'enum' | 'array' | 'number' | 'boolean';
  enumValues?: string[];
  description?: string;
}

export const FIELD_METADATA: Record<FilterableField, FieldMeta> = {
  title: { label: 'Title', type: 'string' },
  description: { label: 'Description', type: 'string' },
  status: {
    label: 'Status',
    type: 'enum',
    enumValues: ['not_started', 'in_progress', 'completed', 'waiting', 'canceled']
  },
  horizon: {
    label: 'Horizon',
    type: 'enum',
    enumValues: ['now', 'next', 'soon', 'later', 'someday']
  },
  deadline: { label: 'Deadline', type: 'date' },
  created_time: { label: 'Created', type: 'date' },
  last_modified_time: { label: 'Last Modified', type: 'date' },
  completed_date: { label: 'Completed Date', type: 'date' },
  parent_id: { label: 'Parent Task', type: 'string' },
  team_id: { label: 'Team', type: 'string' },
  assigned_to_id: { label: 'Assignee', type: 'string' },
  user_id: { label: 'Owner', type: 'string' },
  tags: { label: 'Tags', type: 'array' },
  recurrence_interval: {
    label: 'Recurrence',
    type: 'enum',
    enumValues: ['never', 'daily', 'weekly', 'monthly', 'yearly']
  },
  notes: { label: 'Notes', type: 'string' },
  source: { label: 'Source', type: 'string' },
};

// ============================================================================
// Date Expressions - for relative date queries
// ============================================================================

export type DateExpression =
  | '$today'
  | '$tomorrow'
  | '$yesterday'
  | '$startOfWeek'
  | '$endOfWeek'
  | '$startOfMonth'
  | '$endOfMonth'
  | '$startOfYear'
  | '$endOfYear'
  | `$daysAgo(${number})`
  | `$daysFromNow(${number})`
  | `$weeksAgo(${number})`
  | `$weeksFromNow(${number})`
  | `$monthsAgo(${number})`
  | `$monthsFromNow(${number})`;

// ============================================================================
// Query Conditions
// ============================================================================

// Single condition: field + operator + value
export interface QueryCondition {
  field: FilterableField;
  operator: QueryOperator;
  value?: unknown; // Type depends on operator
}

// Group of conditions with AND/OR logic
export interface QueryGroup {
  logic: 'and' | 'or';
  conditions: QueryNode[];
}

// A query node is either a condition or a group
export type QueryNode = QueryCondition | QueryGroup;

// ============================================================================
// Complete Query with metadata
// ============================================================================

export interface TaskQuery {
  version: typeof QUERY_SCHEMA_VERSION;
  root: QueryNode | null;
  // Optional metadata
  name?: string;
  description?: string;
}

// ============================================================================
// Type Guards
// ============================================================================

export function isQueryGroup(node: QueryNode): node is QueryGroup {
  return 'logic' in node && 'conditions' in node;
}

export function isQueryCondition(node: QueryNode): node is QueryCondition {
  return 'field' in node && 'operator' in node;
}

export function isDateExpression(value: unknown): value is DateExpression {
  return typeof value === 'string' && value.startsWith('$');
}

// ============================================================================
// Query Creation Helpers
// ============================================================================

export function createQuery(root: QueryNode | null = null): TaskQuery {
  return { version: QUERY_SCHEMA_VERSION, root };
}

export function and(...conditions: QueryNode[]): QueryGroup {
  return { logic: 'and', conditions };
}

export function or(...conditions: QueryNode[]): QueryGroup {
  return { logic: 'or', conditions };
}

export function cond(field: FilterableField, operator: QueryOperator, value?: unknown): QueryCondition {
  return { field, operator, value };
}

// Shorthand helpers for common conditions
export const q = {
  eq: (field: FilterableField, value: unknown) => cond(field, 'eq', value),
  neq: (field: FilterableField, value: unknown) => cond(field, 'neq', value),
  in: (field: FilterableField, values: unknown[]) => cond(field, 'in', values),
  notIn: (field: FilterableField, values: unknown[]) => cond(field, 'notIn', values),
  gt: (field: FilterableField, value: unknown) => cond(field, 'gt', value),
  gte: (field: FilterableField, value: unknown) => cond(field, 'gte', value),
  lt: (field: FilterableField, value: unknown) => cond(field, 'lt', value),
  lte: (field: FilterableField, value: unknown) => cond(field, 'lte', value),
  isNull: (field: FilterableField) => cond(field, 'isNull'),
  isNotNull: (field: FilterableField) => cond(field, 'isNotNull'),
  contains: (field: FilterableField, value: string) => cond(field, 'contains', value),
  hasAny: (field: FilterableField, values: unknown[]) => cond(field, 'hasAny', values),
  hasAll: (field: FilterableField, values: unknown[]) => cond(field, 'hasAll', values),
};
